import { useState, type ReactNode } from 'react'
import { buttonVariants, Popover } from '@systemeio/components'
import { ArrowNarrowRightIcon, Bell01Icon, Settings02Icon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next'
import { twMerge } from 'tailwind-merge'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import { useFloatHover } from 'shared/hooks/use-float-hover'
import useUser from 'shared/hooks/use-user'
import DoubleCheckmarkIcon from 'shared/icons/double-checkmark-icon'
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination'
import { isCustomDomain } from 'shared/utils/is-custom-domain'
import { getDashboardNotificationSettingsLink } from 'modules/header/menu/utils'
import NotificationItem from 'modules/profile/notifications/components/notification-item'
import NotificationEmptyList from 'modules/profile/notifications/components/notification-list-empty'
import NotificationsListSkeleton from 'modules/profile/notifications/components/notifications-list-skeleton'
import UnreadIndicator from 'modules/profile/notifications/components/unread-indicator'
import { useNotificationsActions } from 'modules/profile/notifications/hooks/use-notification-actions'
import { useNotifications } from 'modules/profile/notifications/hooks/use-notifications'

export interface NotificationMenuProps {
  className?: string
  isCustomer?: boolean
  iconClassname?: string
  activeMenuIconClassname?: string
}

const defaultFilter = {
  onlyUnRead: false,
}

const NotificationMenu = ({
  className,
  isCustomer = false,
  iconClassname,
  activeMenuIconClassname,
}: NotificationMenuProps): ReactNode => {
  const { t } = useTranslation()
  const { user } = useUser()
  const [shouldFetch, setShouldFetch] = useState(false)

  const { pagination } = useFilterPagination({
    defaultFilter,
    isStaticUrl: true,
    initLimit: 3,
  })
  const { notificationsMeta, notifications, isFetching } = useNotifications({
    shouldFetch,
    limit: 3,
    pagination,
    filter: defaultFilter,
  })
  const { markAllNotificationsAsRead } = useNotificationsActions()

  const { show, close, delayClose, delayOpen, open } = useFloatHover()

  return (
    <div className={twMerge('flex h-full items-center font-inter text-text-primary', className)}>
      <Popover.Root
        open={show}
        onOpenChange={(next: boolean) => {
          if (next) {
            open()
          } else {
            close()
          }
        }}
      >
        <Popover.Trigger
          data-testid="notification-menu-trigger"
          className={buttonVariants({ variant: 'ghost-gray', size: 'icon-sm' })}
          onMouseEnter={() => {
            setShouldFetch(true)
            delayOpen()
          }}
          onMouseLeave={delayClose}
        >
          <div className="relative w-5 text-sm font-bold text-text-primary">
            <Bell01Icon
              className={twMerge(
                'size-[18px] text-text-primary transition hover:text-text-primary',
                iconClassname,
                show && twMerge('text-text-primary', activeMenuIconClassname),
              )}
            />
            {notificationsMeta?.hasUnread && (
              <UnreadIndicator className="absolute top-0 right-0 p-0" iconClassName="size-2" />
            )}
          </div>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Positioner side="bottom" align="end" sideOffset={5}>
            <Popover.Popup className="max-w-none bg-bg-primary p-0">
              <div
                data-testid="notification-menu-panel"
                onMouseEnter={delayOpen}
                onMouseLeave={delayClose}
                className="flex w-[500px] flex-col py-4 text-sm"
              >
                <div className="flex flex-row justify-between px-6">
                  <h4 className="text-[18px] text-text-primary">
                    {t('profile.user_settings.notifications.title')}
                  </h4>
                  <div className="inline-flex flex-row items-baseline gap-1 stroke-text-primary text-text-primary transition hover:stroke-text-link hover:text-text-link">
                    <DoubleCheckmarkIcon />
                    <span className="cursor-pointer" onClick={markAllNotificationsAsRead}>
                      {t('profile.user_settings.notifications.mark_all_read')}
                    </span>
                  </div>
                </div>
                {isFetching ? (
                  <NotificationsListSkeleton />
                ) : notifications?.items.length ? (
                  notifications?.items.map(notification => (
                    <NotificationItem key={notification.id} notification={notification} />
                  ))
                ) : (
                  <NotificationEmptyList />
                )}
                <div className="flex justify-between px-6 pt-4">
                  <LinkWithoutPrefetch
                    className="inline-flex flex-row items-center gap-1 stroke-text-primary primary-link text-text-primary hover:stroke-text-link hover:text-text-link"
                    href={
                      isCustomer && !isCustomDomain
                        ? getDashboardNotificationSettingsLink(user)
                        : '/profile/notification-settings'
                    }
                  >
                    <Settings02Icon width={18} height={18} />
                    <span className="hidden sm:inline">
                      {t('profile.user_settings.manage_notifications.title')}
                    </span>
                  </LinkWithoutPrefetch>
                  <LinkWithoutPrefetch
                    className="inline-flex flex-row items-center gap-3 stroke-text-primary primary-link text-text-primary hover:stroke-text-link hover:text-text-link"
                    href="/profile/notifications"
                  >
                    <h4 className="capitalize">
                      {t('profile.user_settings.notifications.view_all')}
                    </h4>
                    <ArrowNarrowRightIcon className="size-5" />
                  </LinkWithoutPrefetch>
                </div>
              </div>
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
    </div>
  )
}

export default NotificationMenu
